/**
 * Utility functions to detect the platform and environment
 */

/**
 * Detects if the app is running in a native mobile app (via Capacitor)
 * @returns {boolean} true if running in native app, false if in browser
 */
export const isNativeApp = () => {
  // More robust check - Capacitor should have actual platform capabilities
  if (window.Capacitor) {
    // Check if Capacitor is actually running on a native platform
    if (window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
      return true;
    }
    // Check for Capacitor platform info
    if (window.Capacitor.getPlatform) {
      const platform = window.Capacitor.getPlatform();
      return platform === 'ios' || platform === 'android';
    }
  }
  
  // Check for cordova (legacy Capacitor installations might have this)
  if (window.cordova) {
    return true;
  }
  
  // Check user agent for native app indicators
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for common native app webview indicators
  if (userAgent.includes('chessthread') || 
      userAgent.includes('wkwebview') ||
      userAgent.includes('uiwebview')) {
    return true;
  }
  
  // Additional check: if we're in a browser environment, it's not native
  // Native apps typically don't have these browser-specific objects
  if (typeof window !== 'undefined' && 
      window.location && 
      window.location.protocol &&
      (window.location.protocol === 'http:' || window.location.protocol === 'https:')) {
    return false;
  }
  
  return false;
};

/**
 * Detects if the user is on a mobile device (regardless of browser vs native)
 * @returns {boolean} true if on mobile device
 */
export const isMobileDevice = () => {
  return window.innerWidth <= 1000;
};

/**
 * Detects if running in mobile browser (mobile device but not native app)
 * @returns {boolean} true if mobile browser, false otherwise
 */
export const isMobileBrowser = () => {
  return isMobileDevice() && !isNativeApp();
};

/**
 * Gets the current platform context
 * @returns {string} 'native-ios', 'native-android', 'mobile-browser', or 'desktop'
 */
export const getPlatformContext = () => {
  if (isNativeApp()) {
    // Check if iOS or Android
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ios')) {
      return 'native-ios';
    } else if (userAgent.includes('android')) {
      return 'native-android';
    }
    return 'native-app'; // Generic native app
  }
  
  if (isMobileDevice()) {
    return 'mobile-browser';
  }
  
  return 'desktop';
};